import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FiHome, FiUsers, FiPackage, FiShoppingCart, FiFileText, 
  FiTruck, FiTool, FiUsers as FiUsersIcon, FiLogOut, FiMenu, FiX,
  FiBox, FiDollarSign, FiCreditCard
} from 'react-icons/fi';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  
  const menuItems = [
    { path: '/dashboard', label: 'Tableau de bord', icon: FiHome, roles: ['admin', 'caissier', 'technicien'] },
    { path: '/expenses', label: '💳 Dépenses', icon: FiCreditCard, roles: ['admin', 'caissier'] },
    { path: '/clients', label: 'Clients', icon: FiUsers, roles: ['admin', 'caissier'] },
    { path: '/products', label: 'Produits', icon: FiPackage, roles: ['admin', 'caissier'] },
    { path: '/sales/new', label: 'Nouvelle vente', icon: FiShoppingCart, roles: ['admin', 'caissier'] },
    { path: '/sales', label: 'Liste des ventes', icon: FiFileText, roles: ['admin', 'caissier'] },
    { path: '/invoices', label: 'Factures', icon: FiFileText, roles: ['admin', 'caissier'] },
    { path: '/inventory', label: '📦 Inventaires', icon: FiBox, roles: ['admin', 'caissier'] },
    { path: '/debts', label: '💰 Dettes', icon: FiDollarSign, roles: ['admin', 'caissier'] },
    { path: '/suppliers', label: 'Fournisseurs', icon: FiTruck, roles: ['admin'] },
    { path: '/repairs', label: 'Réparations', icon: FiTool, roles: ['admin', 'technicien'] },
    { path: '/users', label: 'Utilisateurs', icon: FiUsersIcon, roles: ['admin'] },
  ];
  
  const filteredMenu = menuItems.filter(item => item.roles.includes(user?.role));
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 bg-white rounded-lg shadow-md">
          {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>
      
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-5 border-b">
          <div className="flex items-center gap-3">
            {!logoError ? (
              <img 
                src="/logo.png" 
                alt="MST Logo" 
                className="w-10 h-10 rounded-lg shadow-md object-cover"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-lg">MST</span>
              </div>
            )}
            <div className="flex flex-col">
              <span className="font-bold text-gray-800 text-sm leading-tight">MULTI SERVICE</span>
              <span className="font-bold text-green-600 text-xs leading-tight">TECHNOLOGIES</span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t">
            <p className="text-sm text-gray-500">Bienvenue,</p>
            <p className="font-semibold text-gray-800">{user?.name}</p>
            <p className="text-xs text-gray-400 capitalize mt-1">
              {user?.role === 'admin' ? 'Administrateur' : user?.role === 'caissier' ? 'Caissier' : 'Technicien'}
            </p>
          </div>
        </div>
        
        <nav className="p-4 overflow-y-auto max-h-[calc(100vh-200px)]">
          {filteredMenu.map((item) => (
            <button 
              key={item.path} 
              onClick={() => { 
                navigate(item.path); 
                setSidebarOpen(false); 
              }} 
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors text-sm
                ${isActive(item.path) ? 'bg-green-50 text-green-600' : 'text-gray-600 hover:bg-gray-50'}
              `}
            >
              <item.icon size={20} className="flex-shrink-0" /> 
              <span>{item.label}</span>
            </button>
          ))}
          <hr className="my-4 border-gray-200" />
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-sm">
            <FiLogOut size={20} /> <span>Déconnexion</span>
          </button>
        </nav>
      </aside>
      
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8"><Outlet /></div>
      </main>
      
      {sidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export default Layout;